import styled from 'styled-components';
import { ArrowUp, ArrowDown, Loader } from 'lucide-react';
import { downloadAsExcel } from '../../../utils/downloadAsExcel';
import InsightSummary from './InsigntSummary';
import { s } from '../components/PeriodStatsPage.styles';
import { useDragScroll } from '../../../utils/useDragScroll';

export type MonthlyTrend = {
  startDate: string; // "YYYY-MM-DD"
  endDate: string; // "YYYY-MM-DD"
  progressed_items: string | number;
  successful_items: string | number;
  success_rate: string | number; // 퍼센트 문자열("22.6") 가능
  price_success_rate: string | number; // 퍼센트 문자열("61.6") 가능
  avg_bidders: string | number;
};

interface ComparisonAnalysisProps {
  periodType: 'week' | 'month' | 'year';
  data: MonthlyTrend[] | null;
  loading?: boolean;
  error?: string | null;
  insights?: Record<string, string[]> | null; // { "긍정적 변화": string[], "우려 요인": string[] }
  insightsLoading?: boolean;
  insightsError?: string | null;
}

/* 숫자 변환 유틸: "12,345"나 "22.6"도 안전 파싱 */
const num = (v: unknown) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return v;
  const n = Number(String(v).replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
};

/* "YYYY-MM-DD" => { y, m } */
const parseYM = (iso?: string) => {
  if (!iso || typeof iso !== 'string') return { y: NaN, m: NaN };
  const [y, m] = iso.split('-');
  return { y: Number(y), m: Number(m) };
};

/* 2025, 9 => "2025.09" */
const fmtYM = (y: number, m: number) =>
  isNaN(y) || isNaN(m) ? '' : `${y}.${String(m).padStart(2, '0')}`;

/* start~end 가 같은 월이면 단일, 다르면 범위 */
const periodLabelFromRange = (startISO?: string, endISO?: string) => {
  const s = parseYM(startISO);
  const e = parseYM(endISO);
  if (isNaN(s.y) || isNaN(s.m) || isNaN(e.y) || isNaN(e.m)) return '';

  const sLabel = fmtYM(s.y, s.m);
  const eLabel = fmtYM(e.y, e.m);
  return s.y === e.y && s.m === e.m ? sLabel : `${sLabel} ~ ${eLabel}`;
};

/* 내부 렌더용 Row */
type Row = {
  period: string; // "YYYY.MM" or "YYYY.MM ~ YYYY.MM"
  progressed_items: number;
  successful_items: number;
  success_rate: number;
  price_success_rate: number;
  avg_bidders: number;
};

// 증감률 표시 컴포넌트
const ChangeIndicator = ({ value }: { value: string }) => {
  const isPositive = value.startsWith('+');
  const isNegative = value.startsWith('-');

  if (isPositive)
    return (
      <Indicator positive>
        <ArrowUp size={20} /> {value.substring(1)}
      </Indicator>
    );

  if (isNegative)
    return (
      <Indicator negative>
        <ArrowDown size={20} /> {value.substring(1)}
      </Indicator>
    );

  return <span>{value}</span>;
};

const NationalAuctionDelta = ({
  periodType,
  data,
  loading,
  error,
  insights,
  insightsLoading,
  insightsError,
}: ComparisonAnalysisProps) => {
  const { ref, onMouseDown, onMouseLeave, onMouseUp, onMouseMove } =
    useDragScroll();

  const sameMonth = (a?: string, b?: string) => {
    if (!a || !b) return false;
    const A = new Date(a);
    const B = new Date(b);
    return A.getFullYear() === B.getFullYear() && A.getMonth() === B.getMonth();
  };

  const parseYM = (iso?: string) => {
    if (!iso) return { y: NaN, m: NaN };
    const [y, m] = iso.split('-');
    return { y: Number(y), m: Number(m) };
  };

  const fmtYM = (y: number, m: number) =>
    isNaN(y) || isNaN(m) ? '' : `${y}.${String(m).padStart(2, '0')}`;

  const periodLabelFromRange = (startISO?: string, endISO?: string) => {
    const s = parseYM(startISO);
    const e = parseYM(endISO);
    if (isNaN(s.y) || isNaN(s.m) || isNaN(e.y) || isNaN(e.m)) return '';
    const sLabel = fmtYM(s.y, s.m);
    const eLabel = fmtYM(e.y, e.m);
    return s.y === e.y && s.m === e.m ? sLabel : `${sLabel} ~ ${eLabel}`;
  };

  const previousMonthLabelOf = (startISO?: string) => {
    const { y, m } = parseYM(startISO);
    if (isNaN(y) || isNaN(m)) return '';
    let py = y,
      pm = m - 1;
    if (pm === 0) {
      py -= 1;
      pm = 12;
    }
    return fmtYM(py, pm);
  };

  // ▼ prev/base 라인 만들기 부분을 이렇게 구성
  const prevRaw = data?.[0];
  const currRaw = data?.[1];

  // “한 달만 선택” 여부: 현재 기간의 start와 end가 같은 달이면 true
  const isSingleMonth = sameMonth(currRaw?.startDate, currRaw?.endDate);

  // 현재 기간 Row
  const baseData = currRaw
    ? {
        period: periodLabelFromRange(currRaw.startDate, currRaw.endDate),
        progressed_items: num(currRaw.progressed_items),
        successful_items: num(currRaw.successful_items),
        success_rate: num(currRaw.success_rate),
        price_success_rate: num(currRaw.price_success_rate),
        avg_bidders: num(currRaw.avg_bidders),
      }
    : null;

  // 이전 기간 Row (라벨만 조건부로 교체)
  const prevData = prevRaw
    ? {
        period: isSingleMonth
          ? previousMonthLabelOf(currRaw?.startDate) // ✅ 한 달만 선택했으면 “현재 전달”
          : periodLabelFromRange(prevRaw.startDate, prevRaw.endDate), // ✅ 여러 달이면 기존 범위 라벨
        progressed_items: num(prevRaw.progressed_items),
        successful_items: num(prevRaw.successful_items),
        success_rate: num(prevRaw.success_rate),
        price_success_rate: num(prevRaw.price_success_rate),
        avg_bidders: num(prevRaw.avg_bidders),
      }
    : null;

  const metrics = [
    { key: 'progressed_items', label: '진행건수', unit: '건' },
    { key: 'successful_items', label: '낙찰건수', unit: '건' },
    { key: 'success_rate', label: '낙찰률', unit: '%' },
    { key: 'price_success_rate', label: '낙찰가율', unit: '%' },
    { key: 'avg_bidders', label: '평균 응찰자 수', unit: '명' },
  ] as const;

  const getChangeString = (current?: number, prev?: number) => {
    if (prev === undefined || prev === 0 || current === undefined) return '-';
    const diff = current - prev;
    const percent = ((diff / prev) * 100).toFixed(1);
    if (diff > 0) return `+${percent}%`;
    if (diff < 0) return `${percent}%`;
    return '0.0%';
  };

  const formatValue = (val: any, unit: string) => {
    if (val === undefined || val === null) return '-';
    if (unit === '%')
      return (
        Number(val).toLocaleString(undefined, { maximumFractionDigits: 1 }) +
        '%'
      );
    return Number(val).toLocaleString();
  };

  const buildRow = (label: string, data: Row | null) => ({
    구분: label,
    기간: data?.period ?? '-',
    진행건수: data?.progressed_items ?? '-',
    낙찰건수: data?.successful_items ?? '-',
    '낙찰률(%)': data ? Number(data.success_rate).toFixed(1) : '-',
    '낙찰가율(%)': data ? Number(data.price_success_rate).toFixed(1) : '-',
    평균응찰자수: data?.avg_bidders ?? '-',
  });

  const getChangePercent = (cur?: number, prev?: number) => {
    if (prev === undefined || prev === 0 || cur === undefined) return '-';
    return (((cur - prev) / prev) * 100).toFixed(1) + '%';
  };

  const buildChangeRow = (
    label: string,
    cur: Row | null,
    prev: Row | null,
  ) => ({
    구분: label,
    기간: '-',
    진행건수: getChangePercent(cur?.progressed_items, prev?.progressed_items),
    낙찰건수: getChangePercent(cur?.successful_items, prev?.successful_items),
    '낙찰률(%)': getChangePercent(cur?.success_rate, prev?.success_rate),
    '낙찰가율(%)': getChangePercent(
      cur?.price_success_rate,
      prev?.price_success_rate,
    ),
    평균응찰자수: getChangePercent(cur?.avg_bidders, prev?.avg_bidders),
  });

  const handleDownloadExcel = () => {
    const rows = [
      buildRow('이전 기간', prevData),
      buildRow('현재 기간', baseData),
      buildChangeRow('현재 기간 증감률', baseData, prevData),
    ];
    downloadAsExcel(rows as any[], '전국_경매_지표_증감률_분석');
  };

  const buildInsightText = (arr?: string[]) => {
    if (insightsLoading) return '인사이트를 불러오는 중…';
    if (insightsError) return insightsError;
    if (!arr || arr.length === 0) return '표시할 인사이트가 없어요.';
    return arr.join('\n\n');
  };

  const positiveText = buildInsightText(insights?.['긍정적 변화']);
  const negativeText = buildInsightText(insights?.['우려 요인']);

  return (
    <s.Wrap>
      <s.Header>
        <s.HeaderRow>
          <s.Img $bg="#E4EDFD">
            <img src="/assets/images/icon/blue_clock.svg" alt="" />
          </s.Img>
          <s.ExcelButton onClick={handleDownloadExcel} aria-label="엑셀 저장">
            <img src="/assets/images/icon/download-black.svg" alt="" />
          </s.ExcelButton>
        </s.HeaderRow>
        <s.TitleBox>
          <h3>전국 경매 지표 증감률 분석</h3>
          <p>전 기간 대비 증감률 분석</p>
        </s.TitleBox>
      </s.Header>
      {loading ? (
        <LoaderWrap>
          <LoaderImg
            src="/assets/images/icon/loader.gif"
            alt="전국 경매 지표"
          />
        </LoaderWrap>
      ) : (
        <>
          <s.TableWrap>
            <TableContainer
              ref={ref}
              onMouseDown={onMouseDown}
              onMouseLeave={onMouseLeave}
              onMouseUp={onMouseUp}
              onMouseMove={onMouseMove}
            >
              <Table>
                <colgroup>
                  <col width={160} />
                  <col width={120} />
                  <col width={120} />
                  <col width={120} />
                  <col width={120} />
                  <col width={120} />
                </colgroup>
                <thead>
                  <tr>
                    <Th>기간</Th>
                    {metrics.map(({ key, label }) => (
                      <Th align="right" key={key}>
                        {label}
                      </Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: '이전 기간', data: prevData },
                    { label: '현재 기간', data: baseData },
                  ].map(({ label, data }) => (
                    <tr key={label}>
                      <Td style={{ color: '#787d87' }}>
                        <TdCol>
                          {label}
                          <span style={{ color: '#424854' }}>
                            {data?.period ?? '-'}
                          </span>
                        </TdCol>
                      </Td>
                      {metrics.map((metric) => {
                        const key = metric.key as keyof Row;
                        return (
                          <Td align="right" key={metric.key}>
                            {data ? formatValue(data[key], metric.unit) : '-'}
                          </Td>
                        );
                      })}
                    </tr>
                  ))}
                  <AvgTr>
                    <TdAvg>현재 기간 증감률</TdAvg>
                    {metrics.map((metric) => {
                      const key = metric.key as keyof Row;
                      return (
                        <TdAvg key={metric.key}>
                          <ChangeIndicator
                            value={getChangeString(
                              baseData ? (baseData[key] as number) : undefined,
                              prevData ? (prevData[key] as number) : undefined,
                            )}
                          />
                        </TdAvg>
                      );
                    })}
                  </AvgTr>
                </tbody>
              </Table>
            </TableContainer>
          </s.TableWrap>

          <InsightSummary
            title="긍정적 변화"
            content={positiveText}
            titleColor="#0C6146"
            contentColor="#424854"
            bg="#DCF7E8"
          />
          <InsightSummary
            title="우려 요인"
            content={negativeText}
            titleColor="#A01B3C"
            contentColor="#424854"
            bg="#FEEFEF"
          />
        </>
      )}
    </s.Wrap>
  );
};

export default NationalAuctionDelta;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
`;

const LoaderImg = styled.img`
  width: 20px;
  height: 20px;
  display: inline-block;
`;

const Table = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  min-width: max(760px, 100%);

  thead th {
    background: #f4f5f7;
  }

  thead th:first-child {
    border-top-left-radius: 8px;
  }

  thead th:last-child {
    border-top-right-radius: 8px;
  }

  tbody tr:not(:last-child) td {
    border-bottom: 1px solid #e8e9ed;
  }
`;

const Th = styled.th<{ align?: 'left' | 'center' | 'right' }>`
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #424854;
  text-align: ${(p) => p.align ?? 'left'};
`;

const Td = styled.td<{ align?: 'left' | 'center' | 'right' }>`
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #424854;
  text-align: ${(p) => p.align ?? 'left'};
  vertical-align: bottom;
`;

const TdCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
`;

const AvgTr = styled.tr`
  background: #f6f9fe;
`;

const TdAvg = styled(Td)`
  font-weight: 500;
  font-size: 15px;
  color: #0c0e12;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const Indicator = styled.span<{ positive?: boolean; negative?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  font-weight: 700;
  color: ${(p) => (p.positive ? '#0B7A58' : p.negative ? '#D0244B' : '#000')};
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  cursor: grab;

  &.grabbing {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
`;
